/** Core Libs */
import style from './blog-page.module.scss';
import React, { useCallback, useEffect, useState } from 'react';

/** API */
import { getArticles } from '@services/blog.service';

/** Components */
import ArticlePreviewMedium from '../article-previews/article-preview-medium/article-preview-medium';
import ArticlePreviewSmall from '../article-previews/article-preview-small/article-preview-small';
import Breadcrumbs from '../../shared/miscellaneous/breadcrumbs/breadcrumbs';
import ArticlePreviewLarge from '../article-previews/article-preview-large/article-preview-large';
import SearchBlock from '../search-block/search-block';
import TopicsBlock from '../topics-block/topics-block';
import ArticlesNotFound from '../articles-not-found-block/articles-not-found-block';
import SubscribeBlock from '../subscribe-block/subscribe-block';
import ReactPaginate from 'react-paginate';
import { ReactSVG } from 'react-svg';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { BLOG_TOPICS, DEFAULT_BLOG_TOPIC } from '@models/blog.topics';
import Image from 'next/image';

/** Constants */
const LIMIT = 8;
const SCROLL_THRESHOLD = 600;
const SEARCH_DEBOUNCE = 200; // milliseconds
const FIRST_PAGE = 1;
const EXCEPTION_TOPIC = DEFAULT_BLOG_TOPIC.slug;
const MAX_WIDTH_DEKSTOP = 1200;
const MEDIA_QUERY_IMG = `(max-width: ${MAX_WIDTH_DEKSTOP}px)`;

let timeoutId: NodeJS.Timeout;

const BlogPage = (props: any) => {
  const router = useRouter();
  const mostPopularArticles = props.popular.slice(0, 3);
  const restPopular = props.popular.slice(3);

  const [articles, setArticles] = useState(props.articles || []);
  const [currentPage, setCurrentPage] = useState<number>(props.page ?? FIRST_PAGE);

  const [topic, setTopic] = useState(props.topic);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(props.total || 0);

  const querryTopic = router.query.topic;
  const querryPage = router.query.page;

  useEffect(() => {
    const containsOnlyNumbers = querryPage ? /^[0-9]+$/.test(querryPage as string) : true;

    const topicSlugs = BLOG_TOPICS.map((topic) => topic.slug);
    const topicRegex = new RegExp(`^(${topicSlugs.join('|')})$`);
    const isValidTopic = querryTopic ? topicRegex.test(querryTopic as string) : true;

    if (!containsOnlyNumbers) {
      router.push('/blog');
    }
  }, [currentPage, topic, router, totalCount]);

  useEffect(() => {
    if (typeof router.query.topic === 'string' && BLOG_TOPICS.some((topic) => topic.slug === router.query.topic)) {
      scrollToBlock('topicsId');
    }
  }, [router.query.topic]);

  function topicChanged(newTopic: string) {
    if (topic === newTopic) {
      return;
    }
    setTotalCount(0);
    setTopic(newTopic);
    handlePageClick({ selected: 0 }, newTopic);
  }

  function topicSearched(query: string) {
    if (query === searchQuery) {
      return;
    }
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      setTotalCount(0);
      setSearchQuery(query);
      handlePageClick({ selected: 0 }, topic, query);
    }, SEARCH_DEBOUNCE);
  }

  function scrollToBlock(blockId: string) {
    const element = document.getElementById(blockId);
    const y = (element?.getBoundingClientRect().top || 0) + window.pageYOffset - 100;
    window.scrollTo({ top: y });
  }

  const articlesRenderFunction = (): JSX.Element => {
    if (articles.length <= 0) {
      return <ArticlesNotFound />;
    } else {
      const aticlesArray = articles.map((article: any) => <ArticlePreviewMedium key={article.id} article={article} />);
      const hrefValue = '/success-stories/wallex';
      const additionalSlideKey = 5;
      const additionalSlide = (
        <Link key={additionalSlideKey} href={hrefValue}>
          <div className={style.succes__img__conteiner}>
            <picture>
              <source srcSet="/assets/icons/success-card-tablet.jpg" media={MEDIA_QUERY_IMG} />
              <img src="/assets/icons/success-card.png" alt="Success card" className={style.responsiveImage} />
            </picture>
          </div>
        </Link>
      );
      aticlesArray.splice(additionalSlideKey, 0, additionalSlide);
      return aticlesArray;
    }
  };

  const getPageCount = () => {
    return !totalCount || totalCount <= LIMIT ? 1 : Math.trunc(totalCount / LIMIT) + +!!(totalCount % LIMIT);
  };

  const arrowPageCalc = (isPrevious: boolean): number => {
    const prevPage = currentPage - 1;
    const nextPage = currentPage + 1;
    return isPrevious ? (prevPage !== 0 ? prevPage : 1) : nextPage < getPageCount() ? nextPage : currentPage;
  };

  const buildQueryParams = (page: number): string => {
    const topicQuery = topic === EXCEPTION_TOPIC ? '' : '?topic=' + topic;
    const pageQuery = page === FIRST_PAGE ? '' : 'page=' + page;
    return `${topicQuery}${!!topicQuery ? (!!pageQuery ? '&' : '') : !!pageQuery ? '?' : ''}${pageQuery}`;
  };

  const arrowElementHandler = (isPrevious = false): JSX.Element => {
    const page = arrowPageCalc(isPrevious);
    return (
      <>
        <Link scroll={false} href={`${router.pathname}${buildQueryParams(page)}`} shallow={true}>
          <ReactSVG
            style={{ maxWidth: '24px', maxHeight: '24px' }}
            src={isPrevious ? '/blog-images/arror-left.svg' : '/blog-images/arror-right.svg'}
          />
        </Link>
      </>
    );
  };

  const changeTopicHandler = useCallback(
    (topic: string) => {
      topicChanged(topic);
    },
    [topic],
  );

  const hrefHandler = (pageIndex: number): string => {
    return `${router.pathname}${buildQueryParams(pageIndex)}`;
  };

  const paginationRender = () => {
    if (getPageCount() > 1) {
      return (
        <>
          <ReactPaginate
            nextLabel={arrowElementHandler()}
            forcePage={currentPage - 1}
            onPageChange={handlePageClick}
            pageRangeDisplayed={5}
            marginPagesDisplayed={1}
            pageCount={getPageCount()}
            previousLabel={arrowElementHandler(true)}
            pageClassName="page-item page-label"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            hrefBuilder={(pageIndex: number) => hrefHandler(pageIndex)}
            breakLabel={
              <div>
                <div className="mobile">{`Page ${currentPage} of ${getPageCount()}`}</div>
                <div className="desktop">...</div>
              </div>
            }
            breakClassName="page-item page-item-break"
            breakLinkClassName="page-link"
            containerClassName="pagination"
            activeClassName="active"
          />
        </>
      );
    } else {
      return null;
    }
  };

  const setQueryParams = (newTopic: string, newestPage: number): void => {
    let query = {};

    if (newTopic !== EXCEPTION_TOPIC) {
      query = Object.assign(query, { topic: newTopic });
    }

    if (newestPage !== FIRST_PAGE) {
      query = Object.assign(query, { page: newestPage });
    }

    router.push({ query }, undefined, { scroll: false }).catch((e) => {
      console.error(e);
    });
  };

  const handlePageClick = (event: any, newTopic: any = topic, search: any = searchQuery) => {
    const newestPage = event.selected + 1;
    setQueryParams(newTopic, newestPage);

    getArticles(true, event.selected * LIMIT, LIMIT, newTopic, search).then((data: any) => {
      if (currentPage === 0) {
        setArticles(data.data);
      } else {
        setArticles([...data.data]);
      }

      setCurrentPage(() => newestPage);
      setTotalCount(data.total);
      scrollToBlock('topicsId');
    });
  };

  return (
    <>
      <Breadcrumbs />
      <section className={style.section}>
        <div className={`${style.container} ${style.container__popBlogs}`}>
          <h1 className={style.container__title}>{props.h1}</h1>
          <div className={style.article__block}>
            <ArticlePreviewLarge article={mostPopularArticles} />
            <div>
              <div className={style.article__mostPopular}>
                <div className={style.article__mostPopular__title}>Must-Reads for Insight</div>
                <div className={style.article__list}>
                  {restPopular.map((pa: any) => (
                    <ArticlePreviewSmall key={pa.id} article={pa} />
                  ))}
                </div>
              </div>
              <SubscribeBlock />
            </div>
          </div>
        </div>
      </section>
      <hr className={style.bottom__line} />

      <section className={`${style.section}`}>
        <div className={`${style.container} ${style.container__blogs}`}>
          <div className={`${style.topics}`}>
            <div className={style.nav__panel} id="topicsId">
              <TopicsBlock onTopicChange={changeTopicHandler} />
              <SearchBlock onSearch={(query: string) => topicSearched(query)} />
            </div>
          </div>
          <div className={style.blogs__wrap}>{articlesRenderFunction()}</div>

          {paginationRender()}
        </div>
      </section>
    </>
  );
};

export default BlogPage;
